import { Outlet, Link, useLocation } from 'react-router-dom'
import { authService } from '../modules/identity/services/auth'
import type { User } from '../types'
import { UserRole } from '../types'
import {
  LayoutDashboard, Users, Phone, BarChart3, BarChart2, Shield, LogOut, Menu, Moon, Sun,
  UserCog, AlertTriangle, MessageSquare, CalendarClock, CalendarDays, CreditCard, FileText,
  History, X, ChevronDown, Percent, Clock, BookUser, ShieldOff, Archive, UserPlus, Bug,
} from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { usePendingDiscounts } from '../contexts/PendingDiscountsContext'
import { useTheme } from '../contexts/TenantThemeContext'
import BrandBadge from './BrandBadge'

// ── Navigation Structure ────────────────────────────────────────────────────

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  roles: UserRole[]
  group: string
  /** If set, the nav item is hidden when this tenant_feature_flags key is falsy */
  addonFlag?: string
}

const NAV_ITEMS: NavItem[] = [
  // Admin core — shown first for BACKEND_ADMIN
  { group: 'Admin',     path: '/admin',                   label: 'Admin Panel',   icon: Shield,        roles: [UserRole.BACKEND_ADMIN] },
  { group: 'Settings',  path: '/communication/templates', label: 'WA Templates',  icon: MessageSquare, roles: [UserRole.BACKEND_ADMIN] },
  { group: 'Admin',     path: '/billing',                 label: 'Billing',       icon: CreditCard,    roles: [UserRole.BACKEND_ADMIN] },

  // Operations
  { group: 'Overview',   path: '/dashboard',    label: 'Dashboard',   icon: LayoutDashboard, roles: Object.values(UserRole) },
  { group: 'Operations', path: '/leads',         label: 'Active Leads', icon: Users,    roles: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.DEALER_PRINCIPAL] },
  { group: 'Operations', path: '/leads/history', label: 'Lead History', icon: Archive,  roles: [UserRole.TELECALLER, UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL] },
  { group: 'Operations', path: '/workspace',     label: 'Workspace',    icon: Phone,    roles: [UserRole.TELECALLER, UserRole.TEAM_LEADER] },
  { group: 'Operations', path: '/calls/history', label: 'Call History',icon: History,         roles: [UserRole.TELECALLER, UserRole.TEAM_LEADER] },
  { group: 'Settings',   path: '/debug-log',     label: 'Debug Log',   icon: Bug,             roles: [UserRole.TELECALLER, UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL, UserRole.BACKEND_ADMIN] },

  // Management
  { group: 'Management', path: '/customers',               label: 'Customers', icon: BookUser,     roles: [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL] },
  { group: 'Management', path: '/workload',                label: 'Workload', icon: BarChart3,     roles: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.DEALER_PRINCIPAL] },
  { group: 'Management', path: '/reports',                 label: 'Reports',  icon: BarChart2,     roles: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.DEALER_PRINCIPAL] },
  { group: 'Management', path: '/team',                    label: 'Team',     icon: UserCog,       roles: [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER] },
  { group: 'Management', path: '/policy/renewal-pipeline', label: 'Renewals', icon: CalendarClock, roles: [UserRole.INSURANCE_HEAD, UserRole.TEAM_LEADER, UserRole.DEALER_PRINCIPAL] },

  // Actions — manager decision queues (post-call action items)
  { group: 'Actions', path: '/discounts',                label: 'OD Discounts',      icon: Percent,   roles: [UserRole.TEAM_LEADER, UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL] },
  { group: 'Actions', path: '/negative-confirmations',   label: 'Neg. Confirmations', icon: ShieldOff, roles: [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL] },

  // Compliance
  { group: 'Compliance', path: '/fraud',      label: 'Fraud Control', icon: AlertTriangle, roles: [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL], addonFlag: 'fraud_detection_enabled' },
  { group: 'Compliance', path: '/audit',      label: 'Audit Trail',   icon: FileText,      roles: [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD, UserRole.BACKEND_ADMIN] },
  { group: 'Compliance', path: '/compliance', label: 'DPDP',          icon: Shield,        roles: [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD] },

  // Settings (dealer-facing)
  { group: 'Overview', path: '/billing/my-subscriptions', label: 'My Plan',     icon: CreditCard,    roles: [UserRole.DEALER_PRINCIPAL] },
  { group: 'Settings', path: '/settings/discounts',       label: 'OD Discount Limits', icon: Percent, roles: [UserRole.DEALER_PRINCIPAL] },
  { group: 'Settings', path: '/settings/telephony',       label: 'Telephony',   icon: Phone,         roles: [UserRole.DEALER_PRINCIPAL] },
  { group: 'Settings', path: '/settings/whatsapp',        label: 'WhatsApp',  icon: MessageSquare, roles: [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD], addonFlag: 'whatsapp_enabled' },
  { group: 'Settings', path: '/settings/working-calendar', label: 'Working Calendar', icon: CalendarDays, roles: [UserRole.DEALER_PRINCIPAL, UserRole.INSURANCE_HEAD] },
  { group: 'Settings', path: '/settings/lead-assignment',  label: 'Lead Assignment',  icon: UserPlus,    roles: [UserRole.DEALER_PRINCIPAL] },
  { group: 'Settings', path: '/settings/scheduler',       label: 'Scheduler', icon: Clock,         roles: [UserRole.BACKEND_ADMIN, UserRole.INSURANCE_HEAD] },
]

const NAV_GROUPS_DEFAULT  = ['Admin', 'Overview', 'Actions', 'Operations', 'Management', 'Compliance', 'Settings'] as const
const NAV_GROUPS_DP       = ['Overview', 'Actions', 'Compliance', 'Management', 'Operations', 'Settings'] as const

function getNavGroups(role?: UserRole): readonly string[] {
  return role === UserRole.DEALER_PRINCIPAL ? NAV_GROUPS_DP : NAV_GROUPS_DEFAULT
}

// ── Brand Logo ───────────────────────────────────────────────────────────────
// Shows the tenant's logoUrl if set; falls back to the Operon mark SVG.

function BrandLogo({ size = 28, logoUrl, appName }: { size?: number; logoUrl: string | null; appName: string }) {
  return <BrandBadge size={size} logoUrl={logoUrl} appName={appName} />
}

// ── User Avatar ─────────────────────────────────────────────────────────────

function UserAvatar({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.38, background: 'var(--brand-700)', color: 'white' }}
    >
      {initials}
    </span>
  )
}

// ── Dark Mode ───────────────────────────────────────────────────────────────

function toggleDarkMode() {
  const isDark = document.documentElement.classList.toggle('dark')
  localStorage.setItem('ddr_theme', isDark ? 'dark' : 'light')
}

// ── Layout ──────────────────────────────────────────────────────────────────

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [user, setUser] = useState<User>(() => authService.getCurrentUser() as User)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const { appName, tagline, logoUrl } = useTheme()
  const { count: pendingDiscountCount } = usePendingDiscounts()

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'))
  }, [])

  // Refresh feature flags from the server once on mount so that add-on
  // changes made by an admin take effect without requiring a re-login.
  useEffect(() => {
    authService.fetchMe().then(setUser).catch(() => {/* non-fatal — keep cached user */})
  }, [])

  // Close user menu on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close mobile sidebar when viewport grows past the lg breakpoint (1024 px).
  // Without this, resizing desktop→mobile→desktop leaves the blur overlay active.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const handler = (e: MediaQueryListEvent) => { if (e.matches) setSidebarOpen(false) }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const handleToggleDark = () => {
    toggleDarkMode()
    setIsDark(d => !d)
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    await authService.logout()
  }

  const featureFlags = (user as User & { tenant_feature_flags?: Record<string, unknown> })?.tenant_feature_flags ?? {}
  const visibleItems = NAV_ITEMS.filter(item =>
    item.roles.includes(user?.role) &&
    (!item.addonFlag || !!featureFlags[item.addonFlag])
  )
  const navGroups    = getNavGroups(user?.role)

  // Pick the single most specific nav item whose path matches the current URL,
  // so a parent route (e.g. /settings) doesn't stay highlighted alongside a
  // more specific child route (e.g. /settings/telephony).
  const activeItem = visibleItems.reduce<NavItem | null>((best, item) => {
    const matches = item.path === '/dashboard'
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + '/')
    if (!matches) return best
    return !best || item.path.length > best.path.length ? item : best
  }, null)

  const isActive = (item: NavItem) => item === activeItem

  return (
    <div className="h-screen flex overflow-hidden bg-surface-body">

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-sidebar flex flex-col
          w-sidebar border-r
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0 lg:static lg:inset-auto
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{
          background: 'var(--sidebar-bg)',
          borderColor: 'var(--sidebar-border)',
        }}
      >
        {/* Logo / brand area */}
        <div
          className="flex items-center justify-between min-h-[56px] px-5 shrink-0"
          style={{ borderBottom: '1px solid var(--sidebar-border)', paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <BrandLogo size={36} logoUrl={logoUrl} appName={appName} />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight truncate" title={appName} style={{ color: 'var(--sidebar-text-active)' }}>
                {appName}
              </p>
              <p className="text-2xs" style={{ color: 'var(--sidebar-section-text)' }}>
                {tagline}
              </p>
            </div>
          </div>
          {/* Mobile close */}
          <button
            className="lg:hidden p-1 rounded-md transition-colors"
            style={{ color: 'var(--sidebar-text)' }}
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navGroups.map(group => {
            const groupItems = visibleItems.filter(i => i.group === group)
            if (groupItems.length === 0) return null

            return (
              <div key={group} className="mb-4">
                {/* Section label */}
                <p
                  className="text-2xs font-semibold uppercase tracking-widest px-3 mb-1.5"
                  style={{ color: 'var(--sidebar-section-text)' }}
                >
                  {group}
                </p>

                {/* Items */}
                {groupItems.map(item => {
                  const Icon = item.icon
                  const active = isActive(item)

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className="group flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium relative transition-colors"
                      style={{
                        color: active
                          ? 'var(--sidebar-text-active)'
                          : 'var(--sidebar-text)',
                        background: active
                          ? 'var(--sidebar-active)'
                          : 'transparent',
                      }}
                      onMouseEnter={e => {
                        if (!active) {
                          (e.currentTarget as HTMLElement).style.background = 'var(--sidebar-hover)'
                          ;(e.currentTarget as HTMLElement).style.color = 'var(--sidebar-text-active)'
                        }
                      }}
                      onMouseLeave={e => {
                        if (!active) {
                          (e.currentTarget as HTMLElement).style.background = 'transparent'
                          ;(e.currentTarget as HTMLElement).style.color = 'var(--sidebar-text)'
                        }
                      }}
                    >
                      {/* Active indicator bar */}
                      {active && (
                        <span
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
                          style={{ background: 'var(--sidebar-active-border)' }}
                        />
                      )}
                      <Icon
                        className="h-4 w-4 shrink-0"
                        style={{ opacity: active ? 1 : 0.7 }}
                      />
                      <span className="truncate flex-1" title={item.label}>{item.label}</span>
                      {item.path === '/discounts' && pendingDiscountCount > 0 && (
                        <span
                          className="shrink-0 text-xs font-bold rounded-full px-1.5 py-0.5 leading-none"
                          style={{ background: 'var(--warning-text)', color: 'white', fontSize: 10 }}
                        >
                          {pendingDiscountCount}
                        </span>
                      )}
                    </Link>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* User profile footer */}
        <div
          className="shrink-0 p-3"
          style={{ borderTop: '1px solid var(--sidebar-border)' }}
          ref={userMenuRef}
        >
          <button
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-colors"
            style={{ color: 'var(--sidebar-text)' }}
            onClick={() => setUserMenuOpen(v => !v)}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--sidebar-hover)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
          >
            <UserAvatar name={user?.full_name ?? '?'} size={28} />
            <div className="flex-1 min-w-0 text-left">
              <p
                className="text-sm font-medium leading-tight truncate"
                title={user?.full_name}
                style={{ color: 'var(--sidebar-text-active)' }}
              >
                {user?.full_name}
              </p>
              <p className="text-2xs truncate" title={user?.role?.replace(/_/g, ' ')} style={{ color: 'var(--sidebar-section-text)' }}>
                {user?.role?.replace(/_/g, ' ')}
              </p>
            </div>
            <ChevronDown
              className="h-3.5 w-3.5 shrink-0 transition-transform"
              style={{ transform: userMenuOpen ? 'rotate(180deg)' : 'none' }}
            />
          </button>

          {/* User dropdown */}
          {userMenuOpen && (
            <div
              className="mt-1 rounded-lg overflow-hidden border"
              style={{
                background: 'var(--sidebar-active)',
                borderColor: 'var(--sidebar-border)',
              }}
            >
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors"
                style={{ color: 'var(--sidebar-text)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--danger-text)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--sidebar-text)' }}
              >
                <LogOut className={`h-4 w-4 ${loggingOut ? 'animate-pulse' : ''}`} />
                {loggingOut ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile sidebar overlay — z-[25] keeps it BELOW the sidebar (z-30) so the blur never covers it */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[25] lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Main content area ─────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top header bar */}
        <header
          className="min-h-[56px] flex items-center px-4 lg:px-6 shrink-0 gap-3"
          style={{
            background: 'var(--surface-card)',
            borderBottom: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
            paddingTop: 'env(safe-area-inset-top)',
          }}
        >
          {/* Mobile menu trigger */}
          <button
            className="lg:hidden btn-icon"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-4.5 w-4.5" />
          </button>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <BrandLogo size={36} logoUrl={logoUrl} appName={appName} />
            <span className="text-sm font-semibold text-content-primary">{appName}</span>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Header right actions */}
          <div className="flex items-center gap-1.5">
            {/* Dark mode toggle */}
            <button
              onClick={handleToggleDark}
              role="switch"
              aria-checked={isDark}
              aria-label="Toggle theme"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                position: 'relative', flexShrink: 0, padding: 0, cursor: 'pointer',
                width: 48, height: 18, borderRadius: 999,
                border: '1px solid var(--border-default)',
                background: isDark ? 'var(--brand-primary)' : 'var(--surface-hover)',
                transition: 'background 0.2s ease',
              }}
            >
              {/* Mode label sits on the track, opposite the knob */}
              <span
                style={{
                  position: 'absolute', top: 0, bottom: 0, display: 'flex', alignItems: 'center',
                  left: isDark ? 7 : 'auto', right: isDark ? 'auto' : 7,
                  fontSize: 7, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                  color: isDark ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {isDark ? 'Dark' : 'Light'}
              </span>

              {/* Sliding knob carries the current mode's icon */}
              <span
                style={{
                  position: 'absolute', top: 2, left: isDark ? 32 : 2,
                  width: 12, height: 12, borderRadius: 999, background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                  transition: 'left 0.2s ease',
                }}
              >
                {isDark
                  ? <Moon style={{ width: 8, height: 8, color: 'var(--brand-primary)' }} fill="currentColor" />
                  : <Sun style={{ width: 8, height: 8, color: '#f59e0b' }} />
                }
              </span>
            </button>

            {/* Separator — only meaningful next to the user chip (hidden on mobile) */}
            <div
              className="w-px h-5 mx-1 hidden md:block"
              style={{ background: 'var(--border-default)' }}
            />

            {/* User chip */}
            <div className="hidden md:flex items-center gap-2">
              <UserAvatar name={user?.full_name ?? '?'} size={26} />
              <div className="hidden lg:block text-right">
                <p className="text-sm font-medium text-content-primary leading-tight">
                  {user?.full_name}
                </p>
                <p className="text-2xs text-content-tertiary">
                  {user?.role?.replace(/_/g, ' ')}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content. The inner div scrolls by default for all pages.
            Pages that manage their own scroll (e.g. TelecallerWorkspace) use
            h-full + overflow-hidden on their root to opt out of this scroll. */}
        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:p-5 lg:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
